<template>
	<div class="c-product" :data-view="gridView ? 'grid' : 'list'">
		<a class="c-product__image" :href="`${getUrl()}`" :title="artikelTitel || 'Product titel'">
			<img :src="imageUrl || placeholderImageUrl" :alt="artikelTitel || 'Product titel'" />
		</a>
		<div class="c-product__info">
			<a class="h4 c-product__info--title" :href="`${getUrl()}`" :title="artikelTitel || 'Product titel'">
				{{ artikelTitel || 'Product titel' }}
			</a>
			<div v-if="priceUnit && priceQuantity" class="c-product__info--priceunit">{{ priceQuantity }} {{ priceUnit }}</div>
			<div class="c-product__info--sku">Artikelnummer: {{ artikelNummer }}</div>
		</div>
		<div v-if="!hidePriceAndShoppingCart" class="c-product__details">
			<Price :is-logged-in="isLoggedIn" :price-promo="pricePromo" :price="price" :price-order-ratio="calcpriceOrderRatio" :price-unit-description="priceUnitDescription" :price-unit-plural-description="priceUnitPluralDescription" :price-quantity="priceQuantity" :price-unit="priceUnit" />
			<Amount :amount="amount" :price-order-ratio="calcpriceOrderRatio" @changeAmount="changeAmount($event)" />

			<button v-if="isLoggedIn" title="In winkelwagen" class="btn btn-success btn-icononly" @click="placeInBasket()">
				<i class="fal fa-shopping-cart" />
			</button>
			<button v-else title="Inloggen" :class="['btn btn-success btn-icononly', { disabled: amount < 1 }]" @click="loginPopup()">
				<i class="fal fa-shopping-cart" />
			</button>

			<div class="c-product__stock">
				<div v-if="quantityNijverdal < 1 && quantityZwolle < 1 && quantityErmelo < 1">
					<div class="c-product__stock--item">
						<template>
							<span class="d-flex align-items-center">
								<span class="flex-grow-1">Leverbaar:</span>
								<i class="far fa-phone text-secondary pl-1"></i>
								<span class="flex-grow-1 text-secondary">Op bestelling</span>
							</span>
						</template>
					</div>
					<div class="c-product__stock--item">
						<template>
							<span class="d-flex align-items-center">
								<span>
									<span class="d-lg-none">Bel</span>
									<a class="d-lg-none" href="tel:038-4221515" title="Bel voor bestelling">038-4221515</a> 
									<span class="d-none d-lg-inline">Bel 038-4221515</span> 
									<span>of</span>
									<a :href="getMailLink" title="Mail voor bestelling">mail ons</a>
								</span>
							</span>
						</template>
					</div>
				</div>
				<div v-else>
					<div class="c-product__stock--item">
						<span class="c-product__stock--item--name">Nijverdal: </span>
						<template v-if="!isLoadingWarehouseQuantities">
							<span v-if="quantityNijverdal > 20" class="d-flex align-items-center text-success">
								<i class="far fa-check"></i>
								<span class="flex-grow-1">{{ quantityNijverdal }} {{priceUnitDescriptionToLower(quantityNijverdal)}} op voorraad</span>
							</span>
							<span v-else-if="quantityNijverdal > 0" class="d-flex align-items-center text-warning">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">{{ quantityNijverdal }} {{priceUnitDescriptionToLower(quantityNijverdal)}} op voorraad</span>
							</span>
							<span v-else class="d-flex align-items-center text-danger">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">Niet op voorraad</span>
							</span>
						</template>
					</div>
					<div class="c-product__stock--item">
						<span class="c-product__stock--item--name">Zwolle: </span>
						<template v-if="!isLoadingWarehouseQuantities">
							<span v-if="quantityZwolle > 20" class="d-flex align-items-center text-success">
								<i class="far fa-check"></i>
								<span class="flex-grow-1">{{ quantityZwolle }} {{ priceUnitDescriptionToLower(quantityZwolle) }} op voorraad</span>
							</span>
							<span v-else-if="quantityZwolle > 0" class="d-flex align-items-center text-warning">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">{{ quantityZwolle }} {{ priceUnitDescriptionToLower(quantityZwolle) }} op voorraad</span>
							</span>
							<span v-else class="d-flex align-items-center text-danger">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">Niet op voorraad</span>
							</span>
						</template>
					</div>
					<div class="c-product__stock--item">
						<span class="c-product__stock--item--name">Ermelo: </span>
						<template v-if="!isLoadingWarehouseQuantities">
							<span v-if="quantityErmelo > 20" class="d-flex align-items-center text-success">
								<i class="far fa-check"></i>
								<span class="flex-grow-1">{{ quantityErmelo }} {{ priceUnitDescriptionToLower(quantityErmelo) }} op voorraad</span>
							</span>
							<span v-else-if="quantityErmelo > 0" class="d-flex align-items-center text-warning">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">{{ quantityErmelo }} {{ priceUnitDescriptionToLower(quantityErmelo) }} op voorraad</span>
							</span>
							<span v-else class="d-flex align-items-center text-danger">
								<i class="far fa-exclamation-triangle"></i>
								<span class="flex-grow-1">Niet op voorraad</span>
							</span>
						</template>
					</div>
				</div>
			</div>
		</div>
		<Popup :show-popup="showAddedToCartPopup" @closePopup="closeCartPopup">
			<template #title>
				<h3>Toegevoegd aan winkelwagen</h3>
			</template>
			<template #content>
				<div class="c-popup__cartitem">
					<div class="c-popup__cartitem__image">
						<img :src="imageUrl || placeholderImageUrl" :alt="artikelTitel || 'Product titel'" />
					</div>
					<div class="c-popup__cartitem__text">
						<h5>{{ artikelTitel || 'Product titel' }}</h5>
						<p>
							Artikelnummer: {{ artikelNummer }}<br />
							Aantal: {{ amount }}
						</p>
					</div>
				</div>
			</template>
			<template #footer>
				<a href="/winkelwagen" class="btn btn-success">
					<i class="fal fa-shopping-cart" />
					<span>Verder naar bestellen</span>
				</a>
				<a class="btn btn-link" @click="closeCartPopup">Verder winkelen</a>
			</template>
		</Popup>
		<Popup :show-popup="showLoginPopup" @closePopup="showLoginPopup = false">
			<template #title>
				<h3>Inloggen vereist</h3>
			</template>
			<template #content>
				<div class="c-popup__content--text">Inloggen vereist om artikelen aan de winkelwagen toe te voegen.</div>
			</template>
			<template #footer>
				<a class="btn btn-success" @click="btnLogin()">
					<i class="fal fa-user" />
					<span>Inloggen</span>
				</a>
				<a class="btn btn-link" @click="showLoginPopup = false">Niet inloggen</a>
			</template>
		</Popup>
	</div>
</template>

<script>
import axios from 'axios';
import Amount from './Product/Amount.vue';
import Price from './Product/Price.vue';
import Popup from './Product/Popup.vue';

export default {
	components: {
		Amount,
		Price,
		Popup
	},
	// mixins: [basketMixins],
	props: {
		gridView: {
			type: Boolean,
			default: false
		},
		inStock: {
			type: Boolean,
			default: false
		},
		price: {
			type: Number,
			required: true
		},
		pricePromo: {
			type: Number,
			required: false,
			default: 0
		},
		label: {
			type: String,
			default: ''
		},
		isLoggedIn: {
			type: Boolean,
			required: true
		},
		artikelTitel: {
			type: String,
			required: true
		},
		artikelNummer: {
			type: String,
			required: true
		},
		priceQuantity: {
			type: Number,
			required: false,
			default: 0
		},
		priceOrderRatio: {
			type: Number,
			required: false,
			default: 0
		},
		urlPart: {
			type: String,
			required: false,
			default: ''
		},
		priceUnit: {
			type: String,
			required: false,
			default: ''
		},
		priceUnitDescription: {
			type: String,
			required: false,
			default: ''
		},
		priceUnitPluralDescription: {
			type: String,
			required: false,
			default: ''
		},
		quantityZwolle: {
			type: Number,
			required: false,
			default: 0
		},
		quantityNijverdal: {
			type: Number,
			required: false,
			default: 0
		},
		quantityErmelo: {
			type: Number,
			required: false,
			default: 0
		},
		imageUrl: {
			type: String,
			required: false,
			default: ''
		},
		placeholderImageUrl: {
			type: String,
			required: false,
			default: '/Assets/Images/noimage.png'
		},
		hidePriceAndShoppingCart: {
			type: Boolean,
			required: false,
			default: false
		},
		isLoadingPromoPrice: {
			type: Boolean,
			required: true,
			default: false
		},
		isLoadingWarehouseQuantities: {
			type: Boolean,
			required: true,
			default: false
		}
	},
	data() {
		return {
			basketClient: null,
			amount: this.calcpriceOrderRatio,
			showAddedToCartPopup: false,
			showLoginPopup: false
		};
	},
	computed: {
		calcpriceOrderRatio() {
			// if priceOrderRatio = 0, make it 1
			return this.priceOrderRatio === 0 ? 1 : this.priceOrderRatio;
		},
		getMailLink(){
			let url = "mailto:verkoop@haenka.nl";
			url += "?subject=Beschikbaarheid '" + this.artikelTitel + '\' met nummer \'' + this.artikelNummer + '\'';
			url += "&body=De link naar het artikel: " + this.getMailArticleUrl();
			return url;
		}
	},
	watch:{
		priceOrderRatio(newValue, oldValue){
			this.amount = newValue;
		}
	},
	created() {
		this.basketClient = axios.create({
			baseURL: '/api/winkelwagen' // = BasketController
		});
		this.basketClient.defaults.headers.common['Content-Type'] = 'application/json';
		this.basketClient.defaults.timeout = 0;
		this.basketClient.defaults.method = 'post';

		// set default amount to priceorderRatio
		this.amount = this.calcpriceOrderRatio;
	},
	methods: {
		getMailArticleUrl(){
			let url = this.getUrl();
			if(url === this.artikelNummer){
				url = window.location.href + "/" + this.artikelNummer;
			}
			return url;
		},
		priceUnitDescriptionToLower(quantity){
			if(quantity > 1){
				return this.priceUnitPluralDescription.toLowerCase() + "";	
			}
			return this.priceUnitDescription.toLowerCase();
		},
		getUrl(){
			if(this.urlPart){
				return window.location.origin + '/assortiment' + this.urlPart + this.artikelNummer;
			}
			return this.artikelNummer;
		},
		loginPopup() {
			this.showLoginPopup = true;
		},
		placeInBasket() {
			const basketId = localStorage.getItem('basketId');
			const basketItem = {
				basketId: basketId,
				itemNo: this.artikelNummer,
				itemUOM: this.artikelNummer,
				itemDescription: this.artikelTitel,
				amount: this.amount,
				standardPrice: this.price,
				customerPrice: this.pricePromo,
				data: JSON.stringify({priceOrderRatio: this.priceOrderRatio, unit: this.priceUnit})
			};
			this.basketClient
				.post('/item/add', basketItem)
				.then(response => {
					this.updateLocalStorageAndDispatchEvent(response.data);
					this.showAddedToCartPopup = true;
				})
				.catch(e => {
					console.error(e);
				});
		},
		closeCartPopup() {
			this.amount = this.calcpriceOrderRatio;
			this.showAddedToCartPopup = false;
		},
		btnLogin() {
			const abplogin = document.getElementById('abp.io');
			if (abplogin) {
				abplogin.click();
			}
		},
		changeAmount(amount) {
			this.amount = amount;
		},
		updateLocalStorageAndDispatchEvent(customerBasket) {
			if (customerBasket == null || customerBasket === '' || typeof customerBasket.BasketItems === 'undefined') {
				return;
			}

			this.amountInBasket = 0;
			for (var i = 0, _len = customerBasket.BasketItems.length; i < _len; i++) {
				this.amountInBasket += customerBasket.BasketItems[i].Amount;
			}
			if(!Number.isInteger(this.amountInBasket)){
				const priceOrderRatioString = this.amountInBasket + '';
				const priceOrderRatioAccuracy = priceOrderRatioString.split('.')[1].length;

				this.amountInBasket = parseFloat(this.amountInBasket.toFixed(priceOrderRatioAccuracy));
			}
			localStorage.setItem('amountInBasket', this.amountInBasket);
			window.dispatchEvent(
				new CustomEvent('haenka-basket-changed', {
					detail: {
						amount: this.amountInBasket
					}
				})
			);
		}
	}
};
</script>
